/* eslint-disable no-return-assign */
/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { useEffect, useRef, useState } from "react";
// eslint-disable-next-line import/no-extraneous-dependencies
import { gsap } from "gsap";

const circles = [
  { color: "#439D5C", initial: { x: 0, y: 0 }, animate: { x: -110, y: 80 } },
  { color: "#EF801C", initial: { x: 0, y: 0 }, animate: { x: -120, y: -20 } },
  { color: "#7C44E4", initial: { x: 0, y: 0 }, animate: { x: -180, y: 50 } },
  { color: "#EF801C", initial: { x: 0, y: 0 }, animate: { x: -220, y: 20 } },
  { color: "#FFCCFF", initial: { x: 0, y: 0 }, animate: { x: -320, y: -50 } },
  { color: "#C4A4F9", initial: { x: 0, y: 0 }, animate: { x: -350, y: 40 } },

  { color: "#439D5C", initial: { x: 0, y: 0 }, animate: { x: 110, y: 60 } },
  { color: "#EF801C", initial: { x: 0, y: 0 }, animate: { x: 170, y: -20 } },
  { color: "#EF801C", initial: { x: 0, y: 0 }, animate: { x: 200, y: 20 } },
  { color: "#C4A4F9", initial: { x: 0, y: 0 }, animate: { x: 280, y: -50 } },
  { color: "#7C44E4", initial: { x: 0, y: 0 }, animate: { x: 310, y: 50 } },
];

const sticks = [
  {
    color: "#F6C80D",
    initial: { x: 0, y: 0, rotate: 20 },
    animate: { x: -220, y: -80 },
  },
  {
    color: "#FFCCFF",
    initial: { x: 0, y: 0, rotate: -40 },
    animate: { x: -260, y: 60 },
  },
  {
    color: "#53ADF2",
    initial: { x: 0, y: 0, rotate: 10 },
    animate: { x: -290, y: 30 },
  },

  {
    color: "#F6C80D",
    initial: { x: 0, y: 0, rotate: -30 },
    animate: { x: 150, y: 30 },
  },
  {
    color: "#EF801C",
    initial: { x: 0, y: 0, rotate: 30 },
    animate: { x: 160, y: -60 },
  },
  {
    color: "#FFCCFF",
    initial: { x: 0, y: 0, rotate: 20 },
    animate: { x: 220, y: 50 },
  },
  {
    color: "#EF801C",
    initial: { x: 0, y: 0, rotate: 30 },
    animate: { x: 260, y: 10 },
  },
];

const curls = [
  {
    src: "/static/react/curl-purple.svg",
    initial: { x: 0, y: 0 },
    animate: { x: -270, y: -40 },
  },
  {
    src: "/static/react/curl-yellow.svg",
    initial: { x: 0, y: 0 },
    animate: { x: -160, y: -30 },
  },
  {
    src: "/static/react/curl-purple-right.svg",
    initial: { x: 0, y: 0 },
    animate: { x: 100, y: -20 },
  },
  {
    src: "/static/react/curl-yellow-right.svg",
    initial: { x: 0, y: 0 },
    animate: { x: 220, y: -50 },
  },
];

const getRandomValue = (min: number, max: number) => {
  return Math.random() * (max - min) + min;
};

type AnimateContainerProps = {
  confettiDuration: number;
  confettiDelay: number;
  animationName: string;
  delays: {
    circles: number;
    sticks: number;
    curls: number;
  };
  dayOfChallenge: number;
};

function ChallengeComplete({
  confettiDuration,
  confettiDelay,
  delays,
  animationName,
  dayOfChallenge,
}: AnimateContainerProps) {
  const streakRef = useRef<HTMLDivElement>(null);
  const numberRef = useRef(null);
  const ropeRef = useRef<HTMLDivElement>(null);

  const circlesRef = useRef<HTMLDivElement[]>([]);
  const sticksRef = useRef<HTMLDivElement[]>([]);
  const curlsRef = useRef<HTMLImageElement[]>([]);

  const handleMouseEnter = (event: React.MouseEvent<HTMLDivElement>) => {
    const newX = getRandomValue(-30, 30);
    const newY = getRandomValue(-30, 30);

    gsap.to(event.currentTarget, {
      x: `+=${newX}`,
      y: `+=${newY}`,
      duration: 1.4,
      ease: "power3.out",
    });
  };

  const animateCircles = (
    circlesRef: React.RefObject<HTMLDivElement[]>,
    duration: number,
    delay: number,
  ) => {
    circles.forEach((circle, index) => {
      if (circlesRef.current && circlesRef.current[index]) {
        gsap.to(circlesRef.current[index], {
          ...circle.animate,
          duration,
          delay,
          opacity: 1,
          yoyo: true,
          ease: "power3.out",
        });
      }
    });
  };

  const animateSticks = (
    sticksRef: React.RefObject<HTMLDivElement[]>,
    duration: number,
    delay: number,
  ) => {
    sticks.forEach((stick, index) => {
      if (sticksRef.current && sticksRef.current[index]) {
        gsap.to(sticksRef.current[index], {
          ...stick.animate,
          duration,
          delay,
          opacity: 1,
          rotate: 720 + stick.initial.rotate,
          yoyo: true,
          ease: "power3.out",
        });
      }
    });
  };

  const animateCurls = (
    curlsRef: React.RefObject<HTMLImageElement[]>,
    duration: number,
    delay: number,
  ) => {
    curls.forEach((curl, index) => {
      if (curlsRef.current && curlsRef.current[index]) {
        gsap.to(curlsRef.current[index], {
          ...curl.animate,
          duration,
          delay,
          opacity: 1,
          rotate: index === 0 || index === 3 ? 720 : -360,
          yoyo: true,
          ease: "power3.out",
        });
      }
    });
  };

  const appearAndBounce = () => {
    const tl = gsap.timeline();
    tl.fromTo(
      streakRef.current,
      { y: -200, opacity: 0 },
      { y: 0, opacity: 1, duration: 1.5, ease: "bounce.out" },
    );

    const tl2 = gsap.timeline({ delay: 0.4 });
    tl2.to(numberRef.current, {
      y: -10,
      duration: 0.5,
      yoyo: true,
      ease: "power1.inOut",
    });
    tl2.to(numberRef.current, {
      y: 0,
      duration: 0.5,
      yoyo: true,
      ease: "bounce.out",
    });
  };

  const goDownAndShake = () => {
    const tl = gsap.timeline();

    tl.fromTo(
      streakRef.current,
      { y: -300 },
      { y: 0, duration: 1.8, ease: "power2.out" },
    );
    tl.to(streakRef.current, {
      x: "+=10",
      yoyo: true,
      repeat: 5,
      duration: 0.1,
      ease: "power1.inOut",
    });

    const tl1 = gsap.timeline({ delay: 2 });
    tl1.to(numberRef.current, { rotation: -20 });
    tl1.to(
      numberRef.current,
      {
        keyframes: [
          { rotation: 15, duration: 0.5, ease: "power1.inOut" },
          { rotation: -10, duration: 0.5, ease: "power1.inOut" },
          { rotation: 0, duration: 0.5, ease: "power1.inOut" },
        ],
        yoyo: true,
      },
      0,
    );

    gsap.to(ropeRef.current, {
      delay: 1.5,
      duration: 1,
      y: -300,
      ease: "power2.out",
    });
    gsap.to(ropeRef.current, { delay: 2, display: "none" });
  };

  const allScale = () => {
    const tl = gsap.timeline();
    tl.fromTo(
      streakRef.current,
      { opacity: 0, scale: 0 },
      { opacity: 1, scale: 1, duration: 1, ease: "elastic.out(1, 0.8)" },
    );

    const tl1 = gsap.timeline({ delay: 0.5 });
    tl1.fromTo(
      numberRef.current,
      { opacity: 0, scale: 0 },
      { opacity: 1, scale: 1, duration: 1, ease: "elastic.out(1, 0.8)" },
    );
  };

  const allBounce = () => {
    const tl = gsap.timeline();
    tl.fromTo(
      streakRef.current,
      { opacity: 0, y: -200 },
      { opacity: 1, y: 0, duration: 1, ease: "bounce.out" },
    );

    const tl1 = gsap.timeline({ delay: 0.5 });
    tl1.fromTo(
      numberRef.current,
      { opacity: 0, y: -200 },
      { opacity: 1, y: 0, duration: 1, ease: "bounce.out" },
    );
  };

  const allRolling = () => {
    const tl = gsap.timeline();
    tl.fromTo(
      streakRef.current,
      { opacity: 0, rotation: 0, x: -200 },
      { opacity: 1, x: 0, rotation: 360, duration: 1, ease: "expo" },
    );

    const tl1 = gsap.timeline({ delay: 0.6 });
    tl1.fromTo(
      numberRef.current,
      { opacity: 0, x: 200, y: 0, rotation: 0 },
      {
        opacity: 1,
        x: 0,
        y: 0,
        rotation: -360,
        duration: 1.8,
        ease: "elastic.out(1, 0.8)",
      },
    );
  };

  useEffect(() => {
    const { circles, sticks, curls } = delays;

    animateCircles(circlesRef, confettiDuration, confettiDelay + circles);
    animateSticks(sticksRef, confettiDuration, confettiDelay + sticks);
    animateCurls(curlsRef, confettiDuration, confettiDelay + curls);
  }, [confettiDuration, confettiDelay, delays]);

  const animations: { [key: string]: () => void } = {
    appearAndBounce,
    goDownAndShake,
    allScale,
    allBounce,
    allRolling,
  };

  useEffect(() => {
    const animationFunc = animations[animationName];
    if (animationFunc) {
      animationFunc();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [animationName]);
  return (
    <>
      <div className='absolute left-1/2 top-8 w-8'>
        {circles.map((element, index) => (
          <div
            onMouseEnter={handleMouseEnter}
            key={`circle-${index}`}
            ref={(el) => (circlesRef.current[index] = el!)}
            className='absolute h-[6px] w-[6px] rounded-full opacity-0 will-change-transform'
            style={{
              backgroundColor: element.color,
            }}
          />
        ))}
        {sticks.map((element, index) => (
          <div
            onMouseEnter={handleMouseEnter}
            key={`stick-${index}`}
            ref={(el) => (sticksRef.current[index] = el!)}
            className='absolute h-[16px] w-[6px] rounded-[2px] opacity-0 will-change-transform'
            style={{
              backgroundColor: element.color,
            }}
          />
        ))}
        {curls.map((element, index) => (
          <img
            onMouseEnter={handleMouseEnter}
            key={`curl-${index}`}
            ref={(el) => (curlsRef.current[index] = el!)}
            src={element.src}
            alt='curl'
            className='absolute opacity-0 will-change-transform'
          />
        ))}
      </div>
      <div className='mb-4'>
        <div
          ref={streakRef}
          className='dark:bg-yellow-dark-default dark:bg-yellow-dark-default relative mx-auto flex h-[100px] w-[100px] items-center justify-center rounded-full bg-yellow will-change-transform'
        >
          {animationName === "goDownAndShake" && (
            <div
              ref={ropeRef}
              className='absolute bottom-full z-10 h-[220px] w-[1px] bg-black dark:bg-white'
            />
          )}
          <div
            ref={numberRef}
            className='flex h-9 w-9 items-center justify-center rounded-full bg-white text-large-h font-extrabold will-change-transform dark:bg-black'
          >
            {dayOfChallenge}
          </div>
        </div>
      </div>
    </>
  );
}

export default ChallengeComplete;
